import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Key, Plus, Trash2, Copy, Check, Loader2, Zap, CreditCard,
  TrendingUp, AlertCircle, X, BookOpen, BarChart3, Coins,
} from "lucide-react";
import logger from "../utils/logger";

const API = `${window.location.origin}/api`;

const CARD = { background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 18 };
const LABEL = { color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 };

export default function APIKeysPage() {
  const [keys, setKeys] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [clientName, setClientName] = useState("");
  const [rateLimit, setRateLimit] = useState(100);
  const [newKey, setNewKey] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [packs, setPacks] = useState([]);
  const [buyForKey, setBuyForKey] = useState(null);   // {id, client_name}
  const [polling, setPolling] = useState(false);
  const [pollMsg, setPollMsg] = useState("");
  const [perKeyUsage, setPerKeyUsage] = useState({}); // {keyId: {by_endpoint, by_day}}
  const [showDocs, setShowDocs] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchAll = useCallback(async () => {
    try {
      const [k, u, p] = await Promise.all([
        axios.get(`${API}/api-keys`, { withCredentials: true }),
        axios.get(`${API}/api-keys/usage`, { withCredentials: true }),
        axios.get(`${API}/credits/packs`),
      ]);
      setKeys(k.data.api_keys || []);
      setUsage(u.data);
      setPacks(p.data.packs || []);
    } catch (e) {
      logger.error("Failed to fetch", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Handle Stripe redirect
  useEffect(() => {
    const sid = searchParams.get("credit_session_id");
    const cancelled = searchParams.get("credit_cancelled");
    if (cancelled) {
      toast.error("Credit purchase cancelled");
      setSearchParams({}, { replace: true });
      return;
    }
    if (!sid) return;
    setPolling(true);
    setPollMsg("Confirming payment with Stripe...");
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/credits/status/${sid}`, { withCredentials: true });
        if (data.payment_status === "paid" && data.credits_applied) {
          setPolling(false); setPollMsg("");
          toast.success(`✓ ${data.credits} credits added to your account!`);
          setSearchParams({}, { replace: true });
          fetchAll();
          return;
        }
        if (data.status === "expired" || attempts > 15) {
          setPolling(false); setPollMsg("");
          toast.message("Payment still processing — refresh in a moment");
          setSearchParams({}, { replace: true });
          return;
        }
        setPollMsg(`Confirming payment (${attempts}/15)...`);
        setTimeout(tick, 2000);
      } catch {
        if (attempts > 15) { setPolling(false); setPollMsg(""); return; }
        setTimeout(tick, 2000);
      }
    };
    tick();
  }, [searchParams, setSearchParams, fetchAll]);

  const createKey = async () => {
    if (!clientName.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api-keys`,
        { client_name: clientName.trim(), rate_limit: rateLimit },
        { withCredentials: true });
      setNewKey(data);
      setClientName(""); setRateLimit(100);
      setShowCreate(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create key");
    } finally { setCreating(false); }
  };

  const revokeKey = async (id) => {
    if (!window.confirm("Revoke this API key? Active integrations will stop working immediately.")) return;
    try {
      await axios.put(`${API}/api-keys/${id}/revoke`, {}, { withCredentials: true });
      toast.success("API key revoked");
      fetchAll();
    } catch { toast.error("Could not revoke key"); }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const buyCredits = async (packId) => {
    try {
      const { data } = await axios.post(`${API}/credits/checkout`,
        { pack_id: packId, api_key_id: buyForKey.id, origin_url: window.location.origin },
        { withCredentials: true });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    }
  };

  const loadKeyUsage = async (keyId) => {
    if (perKeyUsage[keyId]) {
      setPerKeyUsage((prev) => { const c = { ...prev }; delete c[keyId]; return c; });
      return;
    }
    try {
      // Need to pass API key — but the usage endpoint requires the key itself
      // We'll fetch from admin endpoint instead
      const { data } = await axios.get(`${API}/api-keys/usage`, { withCredentials: true });
      const forKey = (data.endpoint_stats || []).filter(s => s._id?.client_id === keyId);
      setPerKeyUsage((prev) => ({ ...prev, [keyId]: { by_endpoint: forKey } }));
    } catch (err) { console.error("[APIKeys] Failed to load per-key usage:", err); }
  };

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}><Loader2 className="animate-spin" size={24} style={{ margin: "0 auto" }} /></div>;

  const totalCredits = keys.filter(k => k.is_active).reduce((s, k) => s + (k.credit_balance || 0), 0);

  return (
    <div data-testid="api-keys-page" style={{ color: "#f1f5f9" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap" style={{ gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }} data-testid="api-keys-title">API Keys & Credits</h1>
          <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 4, margin: 0 }}>
            Manage developer keys for the Rudrik KYC API v1 · 100 free credits per new key
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="view-docs-btn" onClick={() => setShowDocs(s => !s)} style={ghostBtn}><BookOpen size={13} /> API Docs</button>
          <button data-testid="create-api-key-btn" onClick={() => setShowCreate(true)} style={primaryBtn}><Plus size={14} /> Create Key</button>
        </div>
      </div>

      {polling && (
        <div style={{ ...CARD, marginBottom: 16, borderColor: "#3b82f6", background: "rgba(59,130,246,0.05)" }} data-testid="polling-banner">
          <div className="flex items-center gap-3"><Loader2 className="animate-spin" size={16} style={{ color: "#3b82f6" }} />
            <div><div style={{ fontSize: 13, fontWeight: 600 }}>{pollMsg}</div><div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Don't close this tab.</div></div>
          </div>
        </div>
      )}

      {/* Stats */}
      {usage && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
          <Stat icon={Coins} label="Credits Available" value={totalCredits.toLocaleString()} color="#22c55e" testid="stat-credits" />
          <Stat icon={Zap} label="Total API Calls" value={(usage.total_calls || 0).toLocaleString()} color="#3b82f6" testid="stat-total-calls" />
          <Stat icon={TrendingUp} label="Today's Calls" value={(usage.today_calls || 0).toLocaleString()} color="#a855f7" testid="stat-today-calls" />
          <Stat icon={Key} label="Active Keys" value={usage.active_keys || 0} color="#f59e0b" testid="stat-active-keys" />
        </div>
      )}

      {/* New key reveal */}
      {newKey && (
        <div style={{ ...CARD, background: "rgba(59,130,246,0.06)", borderColor: "#3b82f655", marginBottom: 16 }} data-testid="new-key-display">
          <div className="flex items-center justify-between mb-3">
            <strong style={{ color: "#3b82f6", fontSize: 13 }}>✓ API Key Created — Save these now (shown only once)</strong>
            <button onClick={() => setNewKey(null)} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}><X size={14} /></button>
          </div>
          {[
            { label: "API Key", value: newKey.api_key, field: "api_key" },
            { label: "Secret", value: newKey.secret_key, field: "secret_key" },
          ].map(({ label, value, field }) => (
            <div key={field} className="flex items-center gap-2 mb-2">
              <span style={{ fontSize: 11, color: "#94a3b8", minWidth: 70 }}>{label}</span>
              <code style={{ flex: 1, fontSize: 11, color: "#cbd5e1", background: "#080c12", padding: "7px 10px", borderRadius: 5, fontFamily: "ui-monospace, monospace", wordBreak: "break-all" }}>{value}</code>
              <button onClick={() => copyToClipboard(value, field)} data-testid={`copy-${field}`} style={iconBtn}>
                {copiedField === field ? <Check size={12} style={{ color: "#22c55e" }} /> : <Copy size={12} />}
              </button>
            </div>
          ))}
          <div style={{ marginTop: 8, padding: 10, background: "rgba(245,158,11,0.08)", border: "1px solid #f59e0b33", borderRadius: 6, fontSize: 11, color: "#fcd34d" }}>
            <AlertCircle size={11} style={{ display: "inline", marginRight: 5 }} />
            Secret won't be shown again — store it securely. Use the API key as <code>X-API-Key</code> header.
          </div>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div style={{ ...CARD, marginBottom: 16 }} data-testid="create-form">
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, margin: "0 0 12px" }}>New API Key</h3>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <div style={LABEL}>Client Name</div>
              <input data-testid="client-name-input" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="My Production App" style={inputStyle} />
            </div>
            <div>
              <div style={LABEL}>Rate Limit / min</div>
              <input data-testid="rate-limit-input" type="number" value={rateLimit} onChange={e => setRateLimit(Number(e.target.value))} style={inputStyle} />
            </div>
          </div>
          <div className="flex gap-2">
            <button data-testid="confirm-create-key" onClick={createKey} disabled={creating || !clientName.trim()} style={{ ...primaryBtn, opacity: (creating || !clientName.trim()) ? 0.5 : 1 }}>
              {creating ? <Loader2 className="animate-spin" size={13} /> : <Key size={13} />} Create
            </button>
            <button onClick={() => setShowCreate(false)} style={ghostBtn}>Cancel</button>
          </div>
        </div>
      )}

      {/* Buy credits modal */}
      {buyForKey && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }} onClick={() => setBuyForKey(null)} data-testid="buy-credits-modal">
          <div style={{ ...CARD, maxWidth: 700, width: "100%", padding: 24 }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Buy Credits</h3>
              <button onClick={() => setBuyForKey(null)} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}><X size={16} /></button>
            </div>
            <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 0, marginBottom: 16 }}>For key: <strong style={{ color: "#f1f5f9" }}>{buyForKey.client_name}</strong></p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 14 }}>
              {packs.map(p => (
                <div key={p.id} data-testid={`pack-${p.id}`} style={{ background: "#080c12", border: p.savings ? "1px solid #22c55e55" : "1px solid #1e2530", borderRadius: 10, padding: 16, position: "relative" }}>
                  {p.savings && <span style={{ position: "absolute", top: -8, right: 12, background: "#22c55e", color: "white", fontSize: 9, padding: "2px 8px", borderRadius: 3, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>{p.savings}</span>}
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>{p.label}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, marginTop: 6 }}>{p.credits.toLocaleString()}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>credits · ₹{(p.price_inr / p.credits).toFixed(2)}/credit</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#22c55e", marginTop: 10 }}>₹{p.price_inr.toLocaleString()}</div>
                  <button data-testid={`buy-pack-${p.id}`} onClick={() => buyCredits(p.id)} style={{ ...primaryBtn, width: "100%", marginTop: 12, justifyContent: "center" }}><CreditCard size={12} /> Buy</button>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10, color: "#64748b", padding: 10, background: "#080c12", borderRadius: 6 }}>
              <AlertCircle size={10} style={{ display: "inline", marginRight: 4 }} />
              Test mode — use card <code style={{ color: "#22c55e" }}>4242 4242 4242 4242</code>, any future expiry, any CVC.
            </div>
          </div>
        </div>
      )}

      {/* Keys list */}
      <div style={CARD}>
        <h2 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14, margin: "0 0 14px" }}>API Keys ({keys.length})</h2>
        {keys.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 12 }}>
            No keys yet — create your first key to get 100 free credits
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {keys.map(k => {
              const lowBalance = (k.credit_balance || 0) < 20 && k.is_active;
              const ku = perKeyUsage[k.id];
              return (
                <div key={k.id} data-testid={`api-key-row-${k.id}`} style={{ background: "#080c12", border: `1px solid ${lowBalance ? "#f59e0b55" : "#1e2530"}`, borderRadius: 8, padding: 14 }}>
                  <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
                    <div className="flex items-center gap-3" style={{ minWidth: 0, flex: 1 }}>
                      <Key size={16} style={{ color: k.is_active ? "#3b82f6" : "#475569", flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span style={{ fontSize: 13, fontWeight: 600, color: k.is_active ? "#f1f5f9" : "#64748b" }}>{k.client_name}</span>
                          <span style={{ ...pillStyle, ...(k.is_active ? activePill : revokedPill) }}>{k.is_active ? "Active" : "Revoked"}</span>
                          {lowBalance && <span style={{ ...pillStyle, color: "#f59e0b", background: "rgba(245,158,11,0.12)", border: "1px solid #f59e0b55" }}>Low balance</span>}
                        </div>
                        <code style={{ fontSize: 10, color: "#64748b", fontFamily: "ui-monospace, monospace" }}>{k.api_key?.slice(0, 24)}…</code>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
                      <Metric label="Credits" value={(k.credit_balance ?? 0).toLocaleString()} color={lowBalance ? "#f59e0b" : "#22c55e"} />
                      <Metric label="Calls" value={(k.total_calls ?? 0).toLocaleString()} />
                      <Metric label="Limit/min" value={k.rate_limit_per_minute ?? 100} />
                      <div style={{ display: "flex", gap: 6 }}>
                        {k.is_active && (
                          <>
                            <button data-testid={`buy-credits-btn-${k.id}`} onClick={() => setBuyForKey({ id: k.id, client_name: k.client_name })} style={{ ...ghostBtn, color: "#22c55e", borderColor: "#22c55e44" }}>
                              <Coins size={11} /> Top Up
                            </button>
                            <button data-testid={`view-usage-btn-${k.id}`} onClick={() => loadKeyUsage(k.id)} style={ghostBtn}>
                              <BarChart3 size={11} /> {ku ? "Hide" : "Usage"}
                            </button>
                            <button data-testid={`revoke-key-${k.id}`} onClick={() => revokeKey(k.id)} style={{ ...ghostBtn, color: "#ef4444", borderColor: "#ef444444" }}>
                              <Trash2 size={11} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {ku && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #1e2530" }}>
                      <div style={LABEL}>Endpoint Breakdown (last 30 days)</div>
                      {ku.by_endpoint.length === 0 ? (
                        <div style={{ fontSize: 11, color: "#64748b" }}>No calls yet on this key</div>
                      ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                          <thead><tr style={{ borderBottom: "1px solid #1e2530", color: "#64748b" }}>
                            <th style={cellHead}>Endpoint</th><th style={cellHead}>Calls</th><th style={cellHead}>Avg ms</th>
                          </tr></thead>
                          <tbody>{ku.by_endpoint.map((s, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #1e2530" }}>
                              <td style={cell}><code style={{ color: "#3b82f6" }}>{s._id?.endpoint}</code></td>
                              <td style={cell}>{s.count}</td>
                              <td style={cell}>{Math.round(s.avg_response_ms || 0)}</td>
                            </tr>))}</tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Docs Drawer */}
      {showDocs && <ApiDocs onClose={() => setShowDocs(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function Stat({ icon: Icon, label, value, color, testid }) {
  return (
    <div style={CARD} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color }} />
        <span style={LABEL}>{label}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div style={{ textAlign: "right", minWidth: 60 }}>
      <div style={{ ...LABEL, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: color || "#f1f5f9", fontFamily: "ui-monospace, monospace" }}>{value}</div>
    </div>
  );
}

function ApiDocs({ onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }} onClick={onClose} data-testid="api-docs-modal">
      <div style={{ ...CARD, maxWidth: 760, width: "100%", padding: 24, maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Rudrik KYC API v1 — Quick Reference</h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}><X size={16} /></button>
        </div>
        <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 0 }}>Base URL: <code style={{ color: "#22c55e" }}>{window.location.origin}/api/v1</code></p>
        <h4 style={{ fontSize: 12, color: "#f1f5f9", marginTop: 16, marginBottom: 6 }}>Authentication</h4>
        <pre style={preStyle}>{`curl -X POST {{base_url}}/api/v1/pan/verify \\
  -H "X-API-Key: sk_test_..." \\
  -H "Content-Type: application/json" \\
  -d '{"pan":"ABCDE1234F"}'`}</pre>

        <h4 style={{ fontSize: 12, color: "#f1f5f9", marginTop: 16, marginBottom: 6 }}>Endpoints &amp; Credit Cost</h4>
        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
          <thead><tr style={{ borderBottom: "1px solid #1e2530", color: "#64748b" }}>
            <th style={cellHead}>Method</th><th style={cellHead}>Endpoint</th><th style={cellHead}>Cost</th><th style={cellHead}>Purpose</th>
          </tr></thead>
          <tbody>
            {[
              ["POST", "/v1/pan/verify", "2", "Income Tax PAN verification"],
              ["POST", "/v1/mca21/lookup", "5", "Company lookup by CIN or name"],
              ["POST", "/v1/gst/lookup", "2", "GSTIN verification"],
              ["POST", "/v1/ecourts/search", "3", "Litigation history search"],
              ["POST", "/v1/sanctions/screen", "5", "Sanctions + PEP screening"],
              ["POST", "/v1/adverse-media/extract", "5", "Article risk extraction"],
              ["GET", "/v1/account/balance", "0", "Check credit balance"],
              ["GET", "/v1/account/usage", "0", "Last 30-day usage"],
              ["GET", "/v1/pricing", "0", "Pricing (no auth)"],
            ].map(([m, ep, c, p]) => (
              <tr key={ep} style={{ borderBottom: "1px solid #1e2530" }}>
                <td style={cell}><span style={{ background: m === "POST" ? "rgba(34,197,94,0.15)" : "rgba(59,130,246,0.15)", color: m === "POST" ? "#22c55e" : "#3b82f6", border: `1px solid ${m === "POST" ? "#22c55e55" : "#3b82f655"}`, fontSize: 9, padding: "2px 6px", borderRadius: 3, fontWeight: 700 }}>{m}</span></td>
                <td style={cell}><code style={{ color: "#cbd5e1" }}>{ep}</code></td>
                <td style={cell}><code style={{ color: "#f59e0b" }}>{c}</code></td>
                <td style={{ ...cell, color: "#94a3b8" }}>{p}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h4 style={{ fontSize: 12, color: "#f1f5f9", marginTop: 16, marginBottom: 6 }}>Response Errors</h4>
        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
          <tbody>
            {[
              ["401", "Missing or invalid X-API-Key"],
              ["402", "Insufficient credits — top up at /developers"],
              ["403", "API key has been revoked"],
              ["429", "Rate limit exceeded — check Retry-After header"],
            ].map(([code, msg]) => (
              <tr key={code} style={{ borderBottom: "1px solid #1e2530" }}>
                <td style={{ ...cell, color: "#ef4444", fontWeight: 700, fontFamily: "ui-monospace, monospace" }}>{code}</td>
                <td style={{ ...cell, color: "#94a3b8" }}>{msg}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------
const primaryBtn = { background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", border: "none", padding: "8px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 };
const ghostBtn = { background: "transparent", border: "1px solid #1e2530", borderRadius: 6, padding: "6px 11px", color: "#cbd5e1", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 };
const iconBtn = { background: "transparent", border: "1px solid #1e2530", borderRadius: 5, width: 28, height: 28, color: "#94a3b8", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" };
const inputStyle = { width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 11px", color: "#f1f5f9", fontSize: 13, outline: "none", boxSizing: "border-box" };
const pillStyle = { fontSize: 9, padding: "2px 7px", borderRadius: 3, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" };
const activePill = { color: "#22c55e", background: "rgba(34,197,94,0.12)", border: "1px solid #22c55e55" };
const revokedPill = { color: "#ef4444", background: "rgba(239,68,68,0.12)", border: "1px solid #ef444455" };
const cell = { padding: "6px 10px", textAlign: "left" };
const cellHead = { ...cell, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700 };
const preStyle = { background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: 12, color: "#cbd5e1", fontSize: 11, overflowX: "auto", margin: "8px 0" };
